import { getTapColor, ListStatusBadge } from '@hakimo-ui/hakimo/feature-shared';
import { LocationAlarm } from '@hakimo-ui/hakimo/types';
import { HourglassIcon } from '@hakimo-ui/hakimo/ui-elements';
import {
  buildUnifiedMonitoringUrl,
  useApiDataVersion,
} from '@hakimo-ui/hakimo/util';
import { Label } from '@hakimo-ui/shared/ui-base';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { mapStatusToLabelType } from '../../shared/util/common';
import { useUpdateRenderedTime } from './useUpdateRenderedTime';
dayjs.extend(utc);

interface Props {
  alarm: LocationAlarm;
  selected: boolean;
  onClick: () => void;
}

export function ListItem(props: Props) {
  const { alarm, selected, onClick } = props;
  useUpdateRenderedTime(alarm.id);
  // Encodes the "only v3 goes on the wire" rule, so the link below carries a
  // version only when the override is actually set to v3.
  const apiDataVersion = useApiDataVersion();

  const tapBgColor = alarm.tap ? getTapColor(alarm.tap) : null;
  const isEscalated =
    alarm.resolution === 'escalation_open' ||
    alarm.resolution === 'escalation_close';
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Cmd+Click (Mac) or Ctrl+Click (Windows)
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      // buildUnifiedMonitoringUrl owns the bucket rules (`rg`/`scan`/`ent` for
      // v2, `unified` for v3) so this link and the ones UM writes itself can't
      // drift apart.
      const url = buildUnifiedMonitoringUrl(alarm.id, {
        dataVersion: apiDataVersion,
        product: alarm.product,
        partitionKey: dayjs.utc(alarm.alarmTime).format('YYYYMM'),
      });
      window.open(url, '_blank');
    }
    onClick?.();
  };

  return (
    <button
      onClick={handleClick}
      className={clsx(
        'relative flex w-full cursor-pointer items-center gap-4 rounded-md px-4 py-1 outline-none',
        selected
          ? 'bg-onlight-bg-3 dark:bg-ondark-bg-3'
          : 'hover:bg-onlight-bg-3/50 dark:hover:bg-ondark-bg-3/50',
        'animate-highlight dark:animate-dark-highlight'
      )}
    >
      <div className="flex w-full items-stretch">
        <div
          className="font-oswald flex w-16 flex-shrink-0 items-center justify-center text-center text-3xl font-light text-white"
          style={{ backgroundColor: tapBgColor || 'inherit' }}
        >
          {tapBgColor ? (
            <span>{alarm.tap}</span>
          ) : (
            <span className="inline-block h-5 w-5 flex-shrink-0">
              <HourglassIcon />
            </span>
          )}
        </div>
        <div className="flex flex-1 flex-col truncate px-4 py-2">
          <span
            className="text-onlight-text-1 dark:text-ondark-text-1 flex items-center justify-between truncate text-sm font-medium"
            title={alarm.location?.name}
          >
            <span className="overflow-hidden text-ellipsis">
              {alarm.location?.name}
            </span>
            <Label small text={alarm.rawAlarmsCount?.toString()} />
          </span>
          <div className="flex flex-col gap-1">
            <span className="dark:text-ondark-text-2 text-onlight-text-2 flex items-center gap-2 text-left text-xs">
              <span className="font-medium opacity-70">
                {new Date(alarm.alarmTime).toLocaleTimeString()}
              </span>
              <span className="opacity-70">
                {new Date(alarm.alarmTime).toLocaleDateString()}
              </span>
            </span>
            <div className="flex w-full items-center justify-between">
              <ListStatusBadge
                status={alarm.status}
                labelType={mapStatusToLabelType(alarm.status)}
                small
              />
              {isEscalated && (
                <span className="text-onlight-text-2 dark:text-ondark-text-2 text-xs italic">
                  ESCALATED
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

export default ListItem;
